import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
// Hooks
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
// API
import { getWashoutsSidePanelTree } from "@/Washouts/api/common/queries";
// Helpers
import { expandActiveCommissionCycle, refreshSidePanel } from "@/Washouts/helpers/sidePanelTrees";
import { addWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
import { supportedSystemPositions } from "@/Common/helpers/Positions";
// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import AdvisorWashoutContainer from "./AdvisorWashoutContainer";
import WashoutsSideBar from "@/Washouts/layouts/WashoutsSideBar";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";
// Types
import { IActiveCommissionCycle, ISidePanelSelection, TAdvisorWashoutLayerProps, TWashoutTreeCache } from "@/Washouts/types/advisors";
import { ILocation } from "@/Common/interfaces/location";
import { RootState } from "@/redux/store";

const resolvePositionWithoutSpaces = (position: string, positionType: string) => {
	const positions = supportedSystemPositions();
	if (position === positions.PARTS.PARTS_COUNTER_ASSOCIATE) {
		return `PartsCounter${positionType}`;
	}
	return position.replace(/\s/g, "");
};

// Constants
const EMPLOYEE_ORDER_LABEL = "By Employee";
const QUERY_IDENTIFIERS = ["getWashout"];

export default function AdvisorWashoutLayer(props: Readonly<TAdvisorWashoutLayerProps>) {
	const { position = "", actions, positionType } = props;
	const positionWithoutSpaces = resolvePositionWithoutSpaces(position, positionType);
	const [selected, setSelected] = useState<ISidePanelSelection | null>(null);
	// eslint-disable-next-line no-unused-vars
	const [expanded, setExpanded] = useState<string[]>([]);
	const { clearCache } = useCacheCleaner();
	const [toggleTitle, setToggleTitle] = useState(EMPLOYEE_ORDER_LABEL);
	const [open, setOpen] = useState(true);
	const [activeCommissionCycles, setActiveCommissionCycles] = useState<IActiveCommissionCycle[]>([]);

	const { addSubscription, clearSubscriptionContext } = useSubscriptions();

	// Washout subscriptions
	const { useWashoutSubscriptions } = useFactoryWashout();
	const subscribeWashout = useWashoutSubscriptions(position);
	const onCreateWashout = subscribeWashout?.get("onCreateWashout");
	const onUpdateWashout = subscribeWashout?.get("onUpdateWashout");

	const { location, locationId } = useSelector((state: RootState) => state.location);
	const queryClient = useQueryClient();

	const subscriptionsInformation = {
		subscription: {
			context: `${positionWithoutSpaces}WashoutsCommissionCycles`,
			treeContext: `${positionWithoutSpaces}WashoutsTree`,
			hook: addSubscription,
		},
		onNewWashout: {
			context: `onNewAdvisorsWashoutAvailable`,
			query: onCreateWashout?.query,
		},
		onUpdateWashout: {
			context: `onUpdateAdvisorsWashout`,
			query: onUpdateWashout?.query,
		},
	};

	const { data: washoutsSidePanelTree, isLoading } = useQuery({
		queryKey: [`${positionWithoutSpaces}WashoutsTree`, location?.locationId], //logical name for cache entry
		queryFn: async () => {
			const tree = await retrieveAdvisorsWashoutsTreeData();
			if (tree) {
				const nodesToExpand = expandActiveCommissionCycle(tree.tree, tree.activeCommissionCycles, toggleTitle);
				setExpanded(nodesToExpand);
				setActiveCommissionCycles(tree.activeCommissionCycles);
				return tree;
			} else {
				return null;
			}
		},
		staleTime: Infinity,
		enabled: !!location?.locationId,
	});

	const retrieveAdvisorsWashoutsTreeData = async () => {
		const tree = await getWashoutsSidePanelTree(location?.locationId as string, position, positionType);
		if (tree) {
			const activeAndClosingCommissionCycles = await addWashoutSubscriptions(position, tree.tree, subscriptionsInformation);
			setActiveCommissionCycles(activeAndClosingCommissionCycles);
			return {
				tree: tree?.tree || [],
				activeCommissionCycles: activeAndClosingCommissionCycles,
			};
		} else {
			return false;
		}
	};

	const handleSelect = (currentSelected: ISidePanelSelection) => {
		if (currentSelected?._id && currentSelected._id !== selected?._id) {
			setOpen(!open);
			setSelected(currentSelected);
			setExpanded([]);
		}
	};

	const handleOrderBy = (order: string) => {
		setToggleTitle(order);
		setExpanded([]);
	};

	const updateSidePanelCallBack = async (selectedNode: ISidePanelSelection) => {
		const washoutTreeCache: TWashoutTreeCache = queryClient.getQueryData([
			`${positionWithoutSpaces}WashoutsTree`,
			location?.locationId,
		]);
		const { tree = [], activeCommissionCycles: cycles } = washoutTreeCache ?? {};
		if (tree && activeCommissionCycles && selectedNode) {
			// TS Ignore due to JavaScript function with default parameters not interpreting the types correctly
			refreshSidePanel(
				{
					//@ts-ignore
					tree,
					//@ts-ignore
					selectedNode,
					//@ts-ignore
					activeCommissionCycles: cycles,
					//@ts-ignore
					expandedNodes: expanded,
				},
				{
					toggleTitle,
					useRanking: true,
				},
				{
					setExpanded,
					setSelected,
				}
			);
		}
	};

	useEffect(() => {
		if (!location) {
			return () => {};
		}

		return () => {
			setSelected(null);
			setToggleTitle(EMPLOYEE_ORDER_LABEL);
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
			setOpen(true);
		};
	}, [location, position, clearSubscriptionContext]);

	const isTreeLoading = isLoading || locationId !== location?.locationId;

	return (
		<BodyLayer
			floatingContent={
				<CollapsibleLeftPanel
					open={true}
					permanentTrigger={open}
					control={
						<WashoutsSideBar
							treeDefinition={washoutsSidePanelTree}
							loading={isTreeLoading}
							selected={selected?._id ?? ""}
							toggleTitle={toggleTitle}
							onSelectAction={handleSelect}
							onOrderByAction={handleOrderBy}
						/>
					}
				/>
			}
		>
			<AdvisorWashoutContainer
				position={position}
				positionWithoutSpaces={positionWithoutSpaces}
				actions={actions}
				selected={selected}
				dataPresent={Boolean(selected)}
				location={location as ILocation}
				updateSidePanelCallBack={updateSidePanelCallBack}
				activeCommissionCycles={activeCommissionCycles}
				subscriptionsInformation={subscriptionsInformation}
			/>
		</BodyLayer>
	);
}
